import { useState } from 'react'
import BookingModal from '../components/BookingModal'
import FontAwesomeIcon from '../components/FontAwesomeIcon'
import { Link } from 'react-router-dom'

export default function LakeMburo() {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedExperience, setSelectedExperience] = useState('')

  const handleBooking = (experience) => {
    setSelectedExperience(experience)
    setIsModalOpen(true)
  }

  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative h-80 md:h-96 bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(/images/lake-mburo-hero.jpg)',
        }}
      >
        <div className="container mx-auto px-4 text-center text-white">
          <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
            Lake Mburo National Park
          </h1>
          <p className="text-base md:text-lg max-w-2xl mx-auto">
            Uganda's Smallest Savanna Park, Biggest Adventure
          </p>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl font-bold text-safari-green mb-4">Destination Overview</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Lake Mburo National Park lies in western Uganda's Kiruhura District, about 30 km east of Mbarara
                City and just 228 km from Kampala. At 370 square kilometers, it's Uganda's smallest savanna national
                park but delivers exceptional wildlife experiences. The park is unique as the only protected area in
                Uganda with impala and the country's densest zebra populations.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                This biodiverse mosaic of acacia woodlands, savanna grasslands, and wetlands encompasses 5 lakes and
                harbors 68 mammal species and over 350 bird species. The absence of lions and elephants makes Lake
                Mburo Uganda's premier destination for walking safaris and horseback adventures—bringing you closer
                to nature than traditional vehicle-based game drives allow.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Size</h3>
                  <p className="text-gray-700">370 km²</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Distance from Kampala</h3>
                  <p className="text-gray-700">228 km (3.5-4 hours)</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Mammal Species</h3>
                  <p className="text-gray-700">68 species</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Bird Species</h3>
                  <p className="text-gray-700">350+</p>
                </div>
              </div>
            </div>
            <div>
              <img
                src="/images/lake-mburo-hero.jpg"
                alt="Lake Mburo National Park"
                className="w-full h-96 object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How to Get There */}
      <section className="py-16 bg-safari-light">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">How to Get There</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-car" className="w-6 h-6" />
                  By Road from Kampala
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Distance:</strong> 228 km via well-maintained Masaka-Mbarara highway</p>
                  <p><strong>Travel Time:</strong> 3.5-4 hours by private vehicle</p>
                  <p><strong>Entry Points:</strong> Nshara Gate (main entrance) or Sanga Gate (southern entrance)</p>
                  <p className="text-sm italic">4WD recommended during rainy seasons. Regular 2WD sufficient in dry season.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-plane" className="w-6 h-6" />
                  By Air
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Mbarara Airstrip:</strong> Charter flights from Entebbe (45-minute flight). Located 30-minute drive from park.</p>
                  <p><strong>Best For:</strong> Business travelers or those on tight schedules combining multiple destinations.</p>
                  <p className="text-sm italic">Charter flights arranged through tour operators or aviation companies. Ground transfers provided.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-route" className="w-6 h-6" />
                  Strategic Location
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Perfect Stopover:</strong> Ideal break between Kampala and western Uganda parks (Bwindi, Queen Elizabeth)</p>
                  <p><strong>From Rwanda:</strong> 4-5 hours via Katuna border crossing</p>
                  <p><strong>Accessibility:</strong> Most accessible savanna park for short trips from Kampala</p>
                  <p className="text-sm italic">Popular for weekend getaways and multi-park itineraries.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Highlights */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Key Highlights</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-horse" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Zebra Herds</h3>
                <p className="text-gray-700">
                  Lake Mburo boasts Uganda's highest zebra density. Large herds graze openly across golden savanna grasslands, creating spectacular photographic opportunities and unforgettable wildlife encounters.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-ship" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Boat Safaris</h3>
                <p className="text-gray-700">
                  Two-hour boat cruises on Lake Mburo reveal hippos, Nile crocodiles, and spectacular wetland birds including African fish eagles, cormorants, and kingfishers in their natural aquatic habitat.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-person-hiking" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Walking Safaris</h3>
                <p className="text-gray-700">
                  Armed ranger-guided walking safaris provide intimate wildlife encounters impossible from vehicles. The absence of lions makes Lake Mburo one of East Africa's safest parks for on-foot exploration.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-horse-head" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Horseback Safaris</h3>
                <p className="text-gray-700">
                  Experience Africa's wildlife from horseback—a unique perspective that allows remarkably close approaches to zebras, giraffes, and antelope as they recognize horses as non-threatening.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-paw" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Unique Wildlife</h3>
                <p className="text-gray-700">
                  Only Ugandan park with impala populations. Abundant eland, buffalo, warthogs, and giraffes roam acacia woodlands. Recently reintroduced Rothschild giraffes add to wildlife diversity.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-users" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Bahima Culture</h3>
                <p className="text-gray-700">
                  Engage with local Bahima pastoralists famous for their iconic Ankole long-horned cattle. Cultural visits reveal traditional herding practices, milk culture, and ancient warrior heritage.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Activities & Experiences */}
      <section className="py-16 bg-safari-light">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Activities & Experiences</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-person-walking" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Walking Safaris</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Guided walks with armed rangers through open savanna. Safe encounters with zebras, impalas, and warthogs. No lions or elephants make this Uganda's safest walking safari.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 2-3 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Moderate</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $30 per person</p>
                <Link to="/activities/lake-mburo-walking-safaris" className="btn-primary w-full text-center block">
                  View Experience
                </Link>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-horse" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Horseback Safaris</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Ride among zebras, giraffes, and antelopes on well-trained horses. A unique way to get close to wildlife—perfect for all riding levels.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 1-3 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Easy to Moderate</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $80 per person</p>
                <Link to="/activities/lake-mburo-horseback-safaris" className="btn-primary w-full text-center block">
                  View Experience
                </Link>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-bicycle" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Mountain Biking Adventures</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Cycle through the park on designated trails with a ranger guide. Explore off-road routes and enjoy close wildlife encounters on two wheels.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 2-4 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Moderate</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $45 per person</p>
                <Link to="/activities/lake-mburo-mountain-biking" className="btn-primary w-full text-center block">
                  View Experience
                </Link>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-ship" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Boat Trips on Lake Mburo</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Cruise the lake to see hippo pods, Nile crocodiles, and spectacular waterbirds including African fish eagles and kingfishers.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 2 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Easy</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $30 per person</p>
                <Link to="/activities/boat-safaris" className="btn-primary w-full text-center block">
                  View Experience
                </Link>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-car" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Game Drives (Day & Night)</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Safari drives in 4x4 vehicles to spot zebras, buffalos, and Uganda's densest impala population. Night drives reveal nocturnal predators like leopards and hyenas.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 2-4 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Easy</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $60 per person</p>
                <button onClick={() => handleBooking('Game Drives - Lake Mburo')} className="btn-primary w-full">
                  Book Experience
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Travel Tips */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Travel Tips</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6" />
                  Best Time to Visit
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Dry Seasons (December-February, June-September):</strong> Optimal game viewing as animals concentrate around water sources. Roads easily navigable. Best for walking and horseback safaris.</p>
                  <p><strong>Wet Seasons (March-May, October-November):</strong> Lush green landscapes, excellent birding with migrants. Fewer crowds. Some trails may be muddy but still accessible.</p>
                  <p className="text-sm italic">Park accessible year-round. 4WD recommended during heavy rains for comfort.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-suitcase" className="w-6 h-6" />
                  What to Pack
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Essential:</strong> Comfortable hiking shoes, neutral-colored clothing, sunhat, sunscreen, binoculars, camera</p>
                  <p><strong>For Walking Safaris:</strong> Sturdy closed-toe shoes, long pants, water bottle, insect repellent</p>
                  <p><strong>For Horseback:</strong> Long pants, closed shoes with heels, hat with chin strap</p>
                  <p className="text-sm italic">Light layers recommended as temperatures vary. Bring rain jacket year-round.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-heartbeat" className="w-6 h-6" />
                  Health & Safety
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Walking Safaris:</strong> Armed rangers accompany all walks. No lions or elephants makes walking exceptionally safe. Follow ranger instructions.</p>
                  <p><strong>Malaria:</strong> Lower risk but prophylaxis still recommended. Use insect repellent especially at dusk.</p>
                  <p><strong>Horseback Requirements:</strong> Minimum age 12, maximum weight 95kg. Basic riding skills helpful but not required.</p>
                  <p><strong>Fitness:</strong> Moderate fitness sufficient for most activities. Horseback and walking suitable for families.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-coins" className="w-6 h-6" />
                  Budgeting Tips
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Duration:</strong> 2-3 days ideal for experiencing park highlights. Perfect weekend getaway from Kampala.</p>
                  <p><strong>Accommodation:</strong> Range from budget camping ($15-30) to luxury lodges. Book ahead for peak season.</p>
                  <p><strong>Activities:</strong> Game drives and boat safaris most popular. Consider multi-activity packages for better value.</p>
                  <p><strong>Strategic Stop:</strong> Excellent stopover between Kampala and western parks (Bwindi, Queen Elizabeth).</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Where to Stay */}
      <section className="py-16 bg-safari-light">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-6 text-center">Where to Stay</h2>
          <p className="text-center text-gray-700 mb-12 max-w-3xl mx-auto">
            Accommodation options range from basic campsites to luxury lodges, many offering stunning lake views and unique safari experiences.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-lg shadow-lg">
              <thead className="bg-safari-green text-white">
                <tr>
                  <th className="px-6 py-4 text-left">Category</th>
                  <th className="px-6 py-4 text-left">Accommodation Name</th>
                  <th className="px-6 py-4 text-left">Location</th>
                  <th className="px-6 py-4 text-left">Features</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Luxury</td>
                  <td className="px-6 py-4">Mihingo Lodge</td>
                  <td className="px-6 py-4">Rocky hillside inside park</td>
                  <td className="px-6 py-4">Luxury tented suites, infinity pool, spectacular views, gourmet cuisine</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Luxury</td>
                  <td className="px-6 py-4">Kigambira Safari Lodge</td>
                  <td className="px-6 py-4">Hillside overlooking park</td>
                  <td className="px-6 py-4">Private cottages, panoramic views, swimming pool, fine dining</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Mid-Range</td>
                  <td className="px-6 py-4">Rwakobo Rock Lodge</td>
                  <td className="px-6 py-4">Rock kopje outside park</td>
                  <td className="px-6 py-4">Comfortable cottages, restaurant, rock scrambling, good value</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Mid-Range</td>
                  <td className="px-6 py-4">Arcadia Lake Mburo</td>
                  <td className="px-6 py-4">Lakeside inside park</td>
                  <td className="px-6 py-4">Safari tents, lake views, restaurant, bar, boat access</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Budget</td>
                  <td className="px-6 py-4">Rwonyo Rest Camp (UWA)</td>
                  <td className="px-6 py-4">Inside park near HQ</td>
                  <td className="px-6 py-4">Camping, bandas, communal facilities, affordable, convenient</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Budget</td>
                  <td className="px-6 py-4">Leopard Rest Camp</td>
                  <td className="px-6 py-4">Near Sanga Gate</td>
                  <td className="px-6 py-4">Basic bandas, camping, budget-friendly, backpacker atmosphere</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Frequently Asked Questions</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Are there lions or elephants in Lake Mburo?</h3>
                <p className="text-gray-700">
                  No, Lake Mburo has no resident lions or elephants. This makes it Uganda's safest park for walking safaris and horseback riding. You can explore on foot with minimal danger from large predators.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">How safe are walking safaris?</h3>
                <p className="text-gray-700">
                  Very safe. Armed Uganda Wildlife Authority rangers accompany all walks and are trained in wildlife behavior. The absence of lions and elephants makes Lake Mburo exceptionally safe for on-foot exploration compared to other parks.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">How long should I spend at Lake Mburo?</h3>
                <p className="text-gray-700">
                  2-3 days ideal for experiencing key activities: game drives, boat safari, walking safari, and horseback riding. Many visitors use it as a 1-night stopover between Kampala and western parks, which still allows good wildlife viewing.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">What makes Lake Mburo unique?</h3>
                <p className="text-gray-700">
                  It's the only Ugandan park with impala, has the country's highest zebra density, offers safe walking and horseback safaris, and is the most accessible savanna park from Kampala. Perfect for weekend trips and multi-park itineraries.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Do I need horseback riding experience?</h3>
                <p className="text-gray-700">
                  No prior experience required for basic rides. Well-trained horses and experienced guides accommodate beginners. However, some riding confidence helps for longer excursions. Age minimum 12 years, weight maximum 95kg.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">What wildlife will I see?</h3>
                <p className="text-gray-700">
                  Zebras (very common), giraffes, impala, buffalo, eland, warthogs, hippos, crocodiles, and various antelope species. Leopards present but rarely seen. Over 350 bird species including waterbirds and savanna specialists.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Can I combine Lake Mburo with other destinations?</h3>
                <p className="text-gray-700">
                  Absolutely! Lake Mburo is strategically located for western Uganda circuits. Combine with Bwindi (4-5 hours), Queen Elizabeth NP (4 hours), or use as a stopover en route to these parks. Perfect multi-destination itinerary addition.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">When is the best time for boat safaris?</h3>
                <p className="text-gray-700">
                  Year-round activity with consistent wildlife. Early morning and late afternoon offer best lighting and animal activity. Sunset cruises provide magical golden-hour photography and increased bird activity around the lake.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-safari-green text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Experience Uganda's Most Accessible Safari
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Discover the intimate beauty of Lake Mburo National Park where walking safaris and horseback rides bring
            you closer to nature than ever before. From zebra herds to boat cruises among hippos, Lake Mburo delivers
            authentic safari experiences just hours from Kampala.
          </p>
          <Link
            to="/plan-your-trip"
            className="btn-primary text-lg inline-block"
          >
            Plan Your Lake Mburo Adventure
          </Link>
        </div>
      </section>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience={selectedExperience}
      />
    </div>
  )
}
